<template>
  <div class="feedback-container" ref="refFeedbackContainer">
    <ul class="feedback-rate">
      <el-tooltip placement="top" effect="dark" :hide-after="0" :content="$t('Very Dissatisfied')">
        <li :class="{ active: rate === 0 }" @click="rate = 0"><img src="@/assets/icon-feedback-1.png" /></li>
      </el-tooltip>
      <el-tooltip placement="top" effect="dark" :hide-after="0" :content="$t('Dissatisfied')">
        <li :class="{ active: rate === 1 }" @click="rate = 1"><img src="@/assets/icon-feedback-2.png" /></li>
      </el-tooltip>
      <el-tooltip placement="top" effect="dark" :hide-after="0" :content="$t('Neutral')">
        <li :class="{ active: rate === 2 }" @click="rate = 2"><img src="@/assets/icon-feedback-3.png" /></li>
      </el-tooltip>
      <el-tooltip placement="top" effect="dark" :hide-after="0" :content="$t('Satisfied')">
        <li :class="{ active: rate === 3 }" @click="rate = 3"><img src="@/assets/icon-feedback-4.png" /></li>
      </el-tooltip>
      <el-tooltip placement="top" effect="dark" :hide-after="0" :content="$t('Very Satisfied')">
        <li :class="{ active: rate === 4 }" @click="rate = 4"><img src="@/assets/icon-feedback-5.png" /></li>
      </el-tooltip>
    </ul>
    <el-input v-model="email" :placeholder="$t('Your email')" class="email" />
    <el-input v-model="content" resize="none" :placeholder="$t('Is there anything we can help you with?')" class="message" type="textarea" :rows="10" />
    <ul class="file-list" v-if="files.length">
      <li v-for="file, index in files" :key="index" :title="file.name">
        <img class="file-icon" src="@/assets/icon-file.svg" v-svg-inline />
        <span class="file-name text-overflow">{{ file.name }}</span>
        <span class="file-size">{{ formatFileSize(file.size) }}</span>
        <button class="close" @click.stop="handleDeleteFile(index)"><img src="@/assets/icon-close.png" /></button>
        <div class="file-viewer" v-if="['jpg', 'jpeg', 'png', 'gif'].includes(file.ext)">
          <a :href="file.url" target="_blank"><img :src="file.url" alt=""></a>
          <p class="file-name" :title="file.name">{{ file.name }}</p>
          <p class="file-size">{{ formatFileSize(file.size) }}</p>
        </div>
      </li>
    </ul>
    <el-divider class="divider" v-if="files.length"></el-divider>
    <div class="feedback-footer">
      <UploadAppFile
        class="insert-file"
        :uploaded-files="files"
        @on-success="handleUploadSuccess"
        @mouseenter="uploadFileTipVisibility = true"
        @mouseout="uploadFileTipVisibility = false"
        @click="uploadFileTipVisibility = false"
      >
        <img src="@/assets/icon-link.png" alt="">
      </UploadAppFile>
      <div class="btn-group">
        <el-button class="btn" @click="handleCancel">{{ $t('Later') }}</el-button>
        <el-button class="btn" :disabled="!canSubmit" :loading="submitLoading" @click="handleSubmit" type="primary">{{ $t('Submit') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, getCurrentInstance, watch, watchEffect, onMounted } from 'vue'
import { formatFileSize, getOSName, testValue } from '@/libs/tools';
import { appStore, userStore } from '@/stores'
import { ElMessage } from 'element-plus';
import UploadAppFile from '@/components/upload-app-file.vue'
import { feedback } from '@/api/updf-app';
import { postMessageToOpenerWindow } from '@/libs/utils';
import { useElementSize } from '@vueuse/core';
import { getUserInfo } from '@/api/user/user';
const rate = ref(2)
const rateTextArr = ['Very Dissatisfied', 'Dissatisfied', 'Neutral', 'Satisfied', 'Very Satisfied']
const content = ref('')
const email = ref(userStore.userInfo?.email || '')
const files = reactive([])

const emits = defineEmits(['on-close'])

const canSubmit = computed(() => email.value.trim() && content.value.trim())

const { $t } = getCurrentInstance().proxy


const uploadFileTipVisibility = ref(false)
const refFeedbackContainer = ref(null)
const { width, height } = useElementSize(refFeedbackContainer)
const handleUploadSuccess = (file) => {
  files.push(file)
  setTimeout(() => {
    postMessageToOpenerWindow('feedbackReSize', {
      width: width.value,
      height: height.value
    })
  }, 100)
}

watchEffect(() => {
  postMessageToOpenerWindow('feedbackReSize', {
    // 宽高加2是为了防止出现滚动条
    width: width.value ? width.value + 2 : 0,
    height: height.value ? height.value + 2 : 0
  })
})

const handleDeleteFile = index => {
  files.splice(index, 1)
}

const handleCancel = () => {
  emits('on-close')
  postMessageToOpenerWindow('feedbackCancel')
}

const submitLoading = ref(false)
const handleSubmit = async () => {
  if (submitLoading.value) return
  let message = content.value.trim()
  const emailStr = email.value.trim()
  if (!emailStr) {
    ElMessage.error($t('Please correctly fill in your email address.'))
    return false
  }
  if (!testValue('email', emailStr)) {
    ElMessage.error($t('Invalid email address, please re-enter.'))
    return false
  }
  if (!message) {
    ElMessage.error($t('Please provide detailed feedback content.'))
    return false
  }
  submitLoading.value = true
  const remotePaths = files.map(file => {
    return {
      fileName: file.name,
      remotePath: file.remotePath,
    }
  })
  message = `<p>${message}
<br>
<br>
UserAgent: ${navigator.userAgent}
<br>
Rate: ${rateTextArr[rate.value]}
<br>
UID: ${userStore.uid}</p>
  `
  const data = {
    content: message,
    remotePaths,
    system: getOSName() + '-Account',
    build: appStore.version,
    email: emailStr,
    questionType: 'Other'
  }
  submitLoading.value = true
  try {
    await feedback(data)
    ElMessage.success($t('Hello, we have received your feedback and will respond to you as soon as possible. Please be patient, and thank you for your understanding.'))
    content.value = ''
    rate.value = 2
    files.splice(0, files.length)
    emits('on-close')
    postMessageToOpenerWindow('feedbackSuccess')
  } catch (e) {
    console.log(e)
    ElMessage.error(e?.msg || $t('Submission Failed'))
  }
  submitLoading.value = false
}
const handleGetUserInfo = async () => {
  try {
    const { data } = await getUserInfo()
    Object.assign(userStore.userInfo, data.userInfo)
    email.value = data.userInfo?.email || email.value
  } catch (e) {
    console.log(e)
  }
}

onMounted(() => {
  if (!userStore.userInfo?.uid) handleGetUserInfo()
})
</script>

<style lang="scss" scoped>
.feedback-container {
  width: 100%;
  max-width: 420px;
  overflow: hidden;
  .feedback-rate {
    display: flex;
    padding: 8px 8px 16px;
    justify-content: space-between;
    li {
      padding: 7px;
      display: flex;
      justify-content: center;
      align-items: center;
      background: #D9D9D9;
      border-radius: 99px;
      border: 2px solid transparent;
      position: relative;
      box-sizing: border-box;
      height: max-content;
      width: max-content;
      transition: all .3s;
      &:last-child {
        margin-right: 0;
      }
      img {
        filter: grayscale(100%);
        width: 26px;
        transition: all .3s;
        cursor: pointer;
      }
      &:not(.active):hover {
        background: transparent;
        border-color: transparent!important;
        img {
          transform: scale(1.85);
          filter: grayscale(0);
        }
      }
      &.active {
        border-color: #A07EFF;
        img {
          filter: grayscale(0);
        }
      }
    }
  }
  .message {
    margin-top: 12px;
    margin-bottom: 6px;
  }
  .file-list {
    display: flex;
    flex-wrap: wrap;
    position: relative;
    margin-bottom: 20px;
    li {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      background: var(--Button-Secondary-Secondary-Button---Normal, #FFFFFF);
      border: 1px solid var(--Button-Secondary-Secondary-Button---Stroke, #DADADA);
      font-size: 14px;
      padding: 4px;
      margin-right: 10px;
      margin-top: 10px;
      cursor: pointer;
      position: relative;
      max-width: 100%;
      box-sizing: border-box;
      &:hover {
        .file-viewer {
          display: block;
        }
      }
      .file-icon {
        margin-right: 4px;
      }
      .file-name {
        flex: 1;
      }
      .file-size {
        color: var(--Text-Subtitle-Text-Color, #8A8A8A);
        margin-left: 8px;
      }
      .close {
        width: 24px;
        height: 24px;
        display: flex;
        justify-content: center;
        align-items: center;
        background: none;
        border-radius: 4px;
        margin-left: 4px;
        cursor: pointer;
        transition: all .3s;
        border: none;
        img {
          width: 24px;
          height: 24px;
        }
        &:hover {
          background: var(--Icon-Hover-Fill-Color, #0000000F);
        }
        &:active {
          background: var(--Icon-Press-Fill-Color, #0000001F);
        }
      }
      .file-viewer {
        position: absolute;
        left: 0;
        bottom: calc(100% + 4px);
        width: 147px;
        padding: 4px;
        box-sizing: border-box;
        border-radius: 6px;
        background: #353535E5;
        display: none;
        img {
          display: block;
          margin: 0 auto;
          max-width: 100%;
          border-radius: 6px;
        }
        .file-name {
          flex: 1;
          max-width: 200px;
          color: #FFFFFF;
          font-size: 14px;
          margin: 6px 4px 8px;
        }
        .file-size {
          color: #B8B8B8;
          font-size: 14px;
          margin-left: 4px;
        }
      }
    }
  }
  .divider {
    margin: 0;
    margin-bottom: 8px;
  }
  .feedback-footer {
    display: flex;
    justify-content: space-between;
    padding: 8px 0 0;
    .insert-file {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 32px;
      height: 32px;
      margin-left: 0;
      border-radius: 6px;
      cursor: pointer;
      background: none;
      transition: all .3s;
      position: relative;
      font-size: 14px;
      &:hover {
        background: var(--Icon-Hover-Fill-Color, #0000000F);
      }
      &:active {
        background: var(--Icon-Press-Fill-Color, #0000001F);
      }
      img {
        width: 24px;
        height: 24px;
        margin: 0 4px;
      }
      input {
        position: absolute;
        inset: 0;
        opacity: 0;
        cursor: pointer;
      }
    }
    .btn-group {
      .btn {
        height: 32px;
      }
    }
  }
}
@media screen and (max-width: 680px) {
  .feedback-container {
    .file-list {
      li {
        .file-viewer {
          top: auto;
          left: 0;
          bottom: 115%;
        }
      }
    }
  }
}
</style>
<style lang="scss">
.ar-SA {
  .feedback-container {
    .feedback-footer {
      .btn-group {
        .btn {
          margin-left: 0;
        }
      }
    }
    .file-list {
      li {
        margin-right: 0;
        margin-left: 10px;
      }
    }
  }
}
</style>